import {
  CancelOutlined,
  InfoOutlined,
  RefreshOutlined,
} from "@mui/icons-material";
import UploadFile from "@mui/icons-material/UploadFile";
import {
  Box,
  Button,
  Grid,
  Paper,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import TextArea from "antd/es/input/TextArea";
import { addMinutes, isBefore } from "date-fns";
import { useCallback, useEffect, useRef, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { getTimeZonedDate } from "shared/helpers";
import { useAsyncDispatch } from "shared/helpers/hooks/useAsyncDispatch";
import useTranslation from "shared/helpers/hooks/useTranslation";
import ButtonLoader from "shared/ui/ButtonLoader";
import BasicDateTimePicker from "shared/ui/DateTimePicker";
import RightDrawer from "shared/ui/Drawer";
import BasicSelect from "shared/ui/Select";
import { EDistributionMethods } from "store/enums/campaignDetails";
import { ERequestStatus } from "store/enums/index.enum";
import { ICreateOneTimeTrigger } from "store/interfaces/customers";
import {
  GetCampaignById,
  OldDistributionSchedule,
  selectCampaignInfo,
} from "store/slicers/campaignDetail";
import {
  GetCampaignTranslationGrid,
  selectCampaignTranslations,
} from "store/slicers/campaignTranslations";
import {
  CancelOneTimeTrigger,
  CreateOneTimeTrigger,
  GetOneTimeSurvey,
  RescheduleOneTimeTrigger,
} from "store/slicers/customers";
import { read, utils } from "xlsx";

const defaultValues = {
  data: "",
  scheduleTime: new Date(),
  scheduleDateTime: "",
  ignoreQuarantine: false,
  language_id: "",
};

const OneTimeForm = () => {
  const { surveyID } = useParams();
  const [isRescheduleOpen, setRescheduleOpen] = useState(false);
  const dispatch = useAsyncDispatch();
  const [loading, setLoading] = useState(false);
  const [loadingNow, setLoadingNow] = useState(false);
  const [, handleSetPhoneDialogOpen] = useState(false);
  const [, setInvalidPhoneNumbers] = useState([]);
  const fileUpload = useRef<HTMLInputElement>(null);
  const campaignInfo = useSelector(selectCampaignInfo);
  const [activeStep, setActiveStep] = useState(0);
  const [oneTimeData, setOneTimeData] = useState(null);
  const t = useTranslation();

  const languages = useSelector(selectCampaignTranslations);

  const methods = useForm({
    defaultValues,
  });

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    methods.reset({
      ignoreQuarantine: campaignInfo.ignoreQuarantine,
      scheduleTime: addMinutes(new Date(), 1),
      data: methods.watch("data"),
      language_id: methods.watch("language_id"),
    });
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };
 
  const onSubmit = async (formData) => {
    const splitted = formData.data.split("\n");
    const data = splitted.map((item) => {
      const spl = item.split(",");

      return {
        email: spl[0]?.trim() || "",
        customerName: spl[1]?.trim() || "",
        customerSurname: spl[2]?.trim() || "",
        serviceType: spl[3]?.trim() || "",
        phoneNumber: spl[4]?.trim() || "",
        languageID: formData?.language_id,
      };
    });

    const scheduleTime = getTimeZonedDate(formData.scheduleTime);

    const requestData: ICreateOneTimeTrigger = {
      campaignID: Number(surveyID),
      data,
      scheduleTime,
      ignoreQuarantine: formData.ignoreQuarantine,
      distributionMethod: EDistributionMethods.Email,
    };

    const { meta } = await dispatch(CreateOneTimeTrigger(requestData));

    if (meta.requestStatus !== ERequestStatus.FULFILLED) {
      return;
    }

    if (formData.ignoreQuarantine !== campaignInfo.ignoreQuarantine) {
      const data = {
        ignoreQuarantine: formData.ignoreQuarantine,
        triggerIDs: [],
        message: campaignInfo.message,
        numberOfTransaction: campaignInfo.numberOfTransaction,
        postpone: campaignInfo.postpone,
        postponeTime: campaignInfo.postponeTime,
        quarantinePeriod: campaignInfo.quarantinePeriod,
        surveyQuarantine: campaignInfo.surveyQuarantine,
        isLink: campaignInfo.isLink,
      };

      await dispatch(OldDistributionSchedule({ id: Number(surveyID), data }));
    }
    toast.success("Survey has been sent");
  };

  const onPressCsv = () => {
    fileUpload?.current?.click();
  };

  const handleChangeCSV = (e) => {
    e.preventDefault();
    if (e.target.files) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const data = e.target.result;
        const workbook = read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const json = utils.sheet_to_json(worksheet) as Array<{
          email?: string;
          name?: string;
          surname?: string;
          serviceType?: string;
          phone?: string;
        }>;

        let string = "";

        json.forEach((item, index) => {
          string = string.concat(
            `${item.email || ""}, ${item.name || ""}, ${item.surname || ""}, ${item.serviceType || ""}, ${item.phone || ""},`
          );

          if (index !== json.length - 1) {
            string = string.concat("\n");
          }
        });

        methods.reset({
          data: string,
        });
      };
      if (e.target.files?.[0]) {
        reader.readAsArrayBuffer(e.target.files[0]);
      }
    }
  };

  const onSendNow = async () => {
    setLoadingNow(true);
    methods.setValue("scheduleTime", addMinutes(new Date(), 1));
    await methods.handleSubmit(onSubmit)();
    fetchOneTimeData();
    setLoadingNow(false);
  };

  const onSendOnDateTime = async () => {
    setLoading(true);
    await methods.handleSubmit(onSubmit)();
    toast.success("Survey scheduled successfully");
    fetchOneTimeData();
    setLoading(false);
  };

  const onCancel = async () => {
    const { meta } = await dispatch(CancelOneTimeTrigger(surveyID));

    if (meta.requestStatus !== ERequestStatus.FULFILLED) {
      return;
    }

    fetchOneTimeData();
    toast.success("One Time survey canceled successfully");
  };

  const onReschedule = async () => {
    const time = methods.watch("scheduleDateTime");

    if (isBefore(new Date(time), Date.now())) {
      return;
    }

    const scheduleDateTime = getTimeZonedDate(
      methods.watch("scheduleDateTime")
    );

    const requestData = {
      campaignId: surveyID,
      scheduleDateTime,
    };

    const { meta } = await dispatch(RescheduleOneTimeTrigger(requestData));
    if (meta.requestStatus !== ERequestStatus.FULFILLED) {
      return;
    }
    fetchOneTimeData();
    toast.success("One-time survey rescheduled successfully");
    setRescheduleOpen(false);
  };

  const fetchOneTimeData = useCallback(async () => {
    const { meta, payload } = await dispatch(GetOneTimeSurvey(surveyID));

    if (meta.requestStatus !== ERequestStatus.FULFILLED) {
      return;
    }

    setOneTimeData(payload);

    if (payload.count) {
      setActiveStep(-1);
    } else {
      setActiveStep(0);
    }

    if (payload.recipients?.length) {
      setInvalidPhoneNumbers(payload.recipients);
      handleSetPhoneDialogOpen(true);
    } else {
      setInvalidPhoneNumbers([]);
    }

    setTimeout(() => {
      methods.reset(defaultValues);
    }, 0);
  }, [dispatch, methods, surveyID]);

  // const handleCopyNumbers = () => {
  //   navigator.clipboard.writeText(invalidPhoneNumbers.join(","));
  //   toast.success("This numbers are copied to clipboard");
  // };

  const getLanguages = useCallback(() => {
    dispatch(GetCampaignTranslationGrid(surveyID));
  }, [surveyID, dispatch]);

  useEffect(() => {
    getLanguages();
  }, [getLanguages]);

  const init = useCallback(async () => {
    if (!surveyID) return;
    fetchOneTimeData();
    dispatch(GetCampaignById(Number(surveyID)));
  }, [dispatch, fetchOneTimeData, surveyID]);

  useEffect(() => {
    init();
  }, [init]);

  return (
    <FormProvider {...methods}>
      <Box mt={2}>
        <Typography textAlign="center" variant="h5">
          {t("send_one_time_survey")}
        </Typography>
        {oneTimeData?.count > 0 && (
          <Box>
            <Box
              mt={0.5}
              mb={1}
              display="flex"
              justifyContent={"center"}
              alignItems={"center"}
            >
              <InfoOutlined fontSize={"small"} color="action" />
              <Typography>{t("you_have_scheduled_one_time_survey")}</Typography>
            </Box>
            <Box
              display="flex"
              justifyContent={"center"}
              alignItems={"center"}
              gap={2}
              mt={2}
            >
              <ButtonLoader
                isLoading={false}
                onClick={onCancel}
                variant="outlined"
              >
                <Box display={"flex"} alignItems={"center"} gap={1}>
                  <CancelOutlined fontSize={"small"} />
                  <Typography>{t("cancel")}</Typography>
                </Box>
              </ButtonLoader>
              <ButtonLoader
                isLoading={false}
                variant="outlined"
                onClick={() => {
                  methods.setValue(
                    "scheduleDateTime",
                    new Date(oneTimeData.date)
                  );
                  setRescheduleOpen(true);
                }}
              >
                <Box display={"flex"} alignItems={"center"} gap={1}>
                  <RefreshOutlined fontSize={"small"} />
                  <Typography>{t("reschedule")}</Typography>
                </Box>
              </ButtonLoader>
            </Box>
          </Box>
        )}
      </Box>
      <Box display="flex" justifyContent={"center"}>
        <Box component={Paper} p={2} mx={2} mt={2} width="90%">
          <Stepper activeStep={activeStep} orientation="vertical">
            <Step>
              <StepLabel>{t("fill_recipient_info")}</StepLabel>
              <StepContent>
                <Box>
                  <Box mb={2}>
                    <Typography>{t("paste_user_data_below")}</Typography>
                  </Box>
                  <Box flex={1} mb={2}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Controller
                          name="data"
                          control={methods.control}
                          render={({ field }) => (
                            <TextArea
                              {...field}
                              style={{ minHeight: "200px" }}
                              placeholder="john@gmail.com"
                            />
                          )}
                        />
                      </Grid>
                    </Grid>
                  </Box>

                  <Box>
                    <ButtonLoader
                      fullWidth
                      isLoading={false}
                      variant="outlined"
                      onClick={onPressCsv}
                    >
                      <Box>
                        <input
                          type="file"
                          ref={fileUpload}
                          id="upload"
                          accept=".xlsx, .csv"
                          style={{ display: "none" }}
                          onChange={handleChangeCSV}
                        />
                        <UploadFile />
                        <Typography>{t("upload")} CSV / XLSX</Typography>
                      </Box>
                    </ButtonLoader>
                  </Box>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <div>
                    <Button
                      variant="contained"
                      disabled={!methods.watch("data")}
                      onClick={handleNext}
                      sx={{ mt: 1, mr: 1 }}
                    >
                      {t("continue")}
                    </Button>
                  </div>
                </Box>
              </StepContent>
            </Step>
            <Step>
              <StepLabel>{t("select_language")}</StepLabel>
              <StepContent>
                <Box mb={2} sx={{ width: "30%" }}>
                  <BasicSelect
                    options={languages?.displayData}
                    label={"language"}
                    name={"language_id"}
                    labelProp="language"
                    valueProp="languageID"
                  />
                </Box>
                <Box mb={2}>
                  <Button
                    variant="contained"
                    disabled={!methods.watch("language_id")}
                    onClick={handleNext}
                    sx={{ mt: 1, mr: 1 }}
                  >
                    {t("continue")}
                  </Button>
                </Box>
              </StepContent>
            </Step>
            <Step>
              <StepLabel>{t("schedule_delivery_for")}</StepLabel>
              <StepContent>
                <Box display="flex" gap={2}>
                  <BasicDateTimePicker
                    defaultValue={addMinutes(new Date(), 1)}
                    sx={{ width: "100%" }}
                    name={"scheduleTime"}
                    disablePast
                    rules={{
                      required: {
                        value: true,
                        message: "Required",
                      },
                    }}
                    label={"Date and Time"}
                  />
                </Box>
                <Box flex={1}>
                  <Box
                    display="flex"
                    justifyContent={"space-between"}
                    alignItems={"stretch"}
                    mb={2}
                    mt={2}
                  >
                    <Box display="flex" alignItems="center">
                      <Typography>
                        {t("include_the_quarantined_customers")}
                      </Typography>
                    </Box>
                    <Box flex={1}>
                      <Controller
                        name="ignoreQuarantine"
                        control={methods.control}
                        render={({ field }) => (
                          <Tooltip
                            placement="top-start"
                            title={
                              "In case activation of this function the survey will be sent the quarantined customers as well after receiving the trigger."
                            }
                          >
                            <Switch
                              color="success"
                              {...field}
                              checked={field.value}
                            />
                          </Tooltip>
                        )}
                      />
                    </Box>
                  </Box>
                </Box>
                <Box
                  sx={{ mb: 2 }}
                  display="flex"
                  gap={2}
                  alignItems={"center"}
                >
                  <ButtonLoader
                    isLoading={loading}
                    variant="contained"
                    onClick={onSendOnDateTime}
                  >
                    <Typography>{t("schedule_sending")}</Typography>
                  </ButtonLoader>

                  <ButtonLoader
                    isLoading={loadingNow}
                    variant="outlined"
                    onClick={onSendNow}
                  >
                    <Typography>{t("send_now")}</Typography>
                  </ButtonLoader>

                  <Button onClick={handleBack}>{t("back")}</Button>
                </Box>
              </StepContent>
            </Step>
          </Stepper>
        </Box>
      </Box>
      <RightDrawer
        open={isRescheduleOpen}
        setOpen={() => setRescheduleOpen(false)}
        title={"Reschedule One-time survey"}
      >
        <Box display="flex" gap={2}>
          <BasicDateTimePicker
            defaultValue={addMinutes(new Date(), 1)}
            sx={{ width: "100%" }}
            disablePast
            name={"scheduleDateTime"}
            rules={{
              required: {
                value: true,
                message: "Required",
              },
            }}
            label={t("date_and_time")}
          />
        </Box>
        <Button
          variant="contained"
          onClick={onReschedule}
          sx={{ mt: 1, mr: 1 }}
        >
          {t("submit")}
        </Button>
      </RightDrawer>
    </FormProvider>
  );
};

export default OneTimeForm;
